/**
 * Connectors — admin surface for knowledge sources: what syncs into the index
 * and which groups may read the result, grouped by system. Documents inherit
 * their source's group tags; SharePoint files are further limited to the
 * people who can open them. Adding, tag changes and syncs are audit-logged.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSource,
  deleteSource,
  fetchSources,
  patchSource,
  syncSource,
  type NewSource,
  type SourceInfo,
} from '../lib/connectors';
import { ApiError } from '../lib/api';

function fmtTs(ts: string | null): string {
  if (!ts) return 'never';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

const SECTIONS: { kind: string; title: string }[] = [
  { kind: 'sharepoint', title: 'SharePoint' },
  { kind: 'confluence', title: 'Confluence' },
  { kind: 'bitbucket', title: 'Bitbucket' },
];

function sectionTitle(kind: string): string {
  const known = SECTIONS.find((s) => s.kind === kind.replace('-fixture', ''));
  const title = known?.title ?? kind;
  return kind.endsWith('-fixture') ? `${title} (sample)` : title;
}

/** Where a source reads from, in words. */
function location(source: SourceInfo): string | null {
  const c = source.config ?? {};
  if (source.kind === 'sharepoint') {
    if (c.folder) return c.site === '/' ? `Main site: ${c.folder}` : c.folder;
    return c.site ? `Site ${c.site}` : null;
  }
  if (source.kind === 'confluence' && c.space) return `Space ${c.space}`;
  if (source.kind === 'bitbucket' && c.repo) {
    return `${c.project} / ${c.repo}${c.branch ? ` (${c.branch})` : ''}`;
  }
  return null;
}

function SourceCard({ source }: { source: SourceInfo }): ReactNode {
  const qc = useQueryClient();
  const [tagInput, setTagInput] = useState('');

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['sources'] });
  const syncing = source.last_sync_status?.startsWith('syncing') ?? false;
  const patch = useMutation({
    mutationFn: (p: { readable_groups?: string[]; enabled?: boolean }) =>
      patchSource(source.id, p),
    onSuccess: invalidate,
  });
  const sync = useMutation({
    mutationFn: () => syncSource(source.id),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => deleteSource(source.id),
    onSuccess: invalidate,
  });

  const confirmRemove = () => {
    const ok = window.confirm(
      `Remove "${source.display_name}" and delete all its indexed content? ` +
        'The assistant will no longer see documents from this source.',
    );
    if (ok) remove.mutate();
  };

  const removeTag = (g: string) =>
    patch.mutate({ readable_groups: source.readable_groups.filter((x) => x !== g) });
  const addTag = () => {
    const g = tagInput.trim();
    if (!g || source.readable_groups.includes(g)) return;
    patch.mutate({ readable_groups: [...source.readable_groups, g] });
    setTagInput('');
  };

  return (
    <div className="rounded-lg border border-steel-200 bg-white p-4" data-testid="source-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-navy-900">{source.display_name}</p>
          {location(source) && (
            <p className="mt-0.5 text-xs text-steel-600">{location(source)}</p>
          )}
          <p className="mt-0.5 text-xs text-steel-500">
            Last sync: {fmtTs(source.last_sync_at)}
            {source.last_sync_status ? ` — ${source.last_sync_status}` : ''}
          </p>
          {source.kind === 'sharepoint' && (
            <p className="mt-0.5 text-xs text-steel-500">
              File permissions last checked: {fmtTs(source.permissions_checked_at ?? null)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => patch.mutate({ enabled: !source.enabled })}
            className={`rounded px-2 py-1 text-xs font-medium ${
              source.enabled
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-steel-100 text-steel-600'
            }`}
          >
            {source.enabled ? 'Enabled' : 'Disabled'}
          </button>
          <button
            type="button"
            onClick={() => sync.mutate()}
            disabled={sync.isPending || !source.enabled || syncing}
            className="rounded-lg bg-navy-700 px-3 py-1 text-sm font-medium text-white hover:bg-navy-800 disabled:opacity-50"
          >
            {sync.isPending || syncing ? 'Syncing…' : 'Sync now'}
          </button>
          <button
            type="button"
            onClick={confirmRemove}
            disabled={remove.isPending}
            title="Remove this source and delete its indexed content"
            className="rounded-lg border border-red-200 px-2 py-1 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            {remove.isPending ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>

      <div className="mt-3">
        <p className="text-xs font-medium uppercase tracking-wide text-steel-500">
          Readable by
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {source.readable_groups.map((g) => (
            <span
              key={g}
              className="inline-flex items-center gap-1 rounded bg-steel-100 px-2 py-0.5 text-xs text-steel-700"
            >
              {g}
              <button
                type="button"
                aria-label={`Remove ${g}`}
                onClick={() => removeTag(g)}
                className="text-steel-400 hover:text-red-600"
              >
                ✕
              </button>
            </span>
          ))}
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTag()}
            placeholder="+ add group"
            className="w-28 rounded border border-steel-200 px-2 py-0.5 text-xs focus:border-navy-400 focus:outline-none"
          />
        </div>
      </div>

      {sync.data && (
        <p className="mt-2 text-xs text-emerald-700">
          {sync.data.status === 'started'
            ? 'Sync started; progress shows above.'
            : `Synced ${sync.data.documents} documents (${sync.data.chunks} chunks).`}
        </p>
      )}
      {(patch.error || sync.error) && (
        <p className="mt-2 text-xs text-rose-600">
          {(patch.error ?? sync.error) instanceof ApiError
            ? (patch.error ?? sync.error)?.message
            : 'Operation failed.'}
        </p>
      )}
    </div>
  );
}

const FIELDS: Record<NewSource['kind'], { name: string; label: string; hint?: string }[]> = {
  sharepoint: [{
    name: 'site',
    label: 'SharePoint address',
    hint: "Paste a site address, a library view, or a folder's Copy link. On the main site, pick a folder or library.",
  }],
  confluence: [{ name: 'space', label: 'Space key', hint: 'As in the space URL, e.g. EH' }],
  bitbucket: [
    { name: 'project', label: 'Project key', hint: 'e.g. SES-CFS' },
    { name: 'repo', label: 'Repository', hint: 'e.g. ses-cfs-docs (documentation files only)' },
    { name: 'branch', label: 'Branch (optional)', hint: 'Default branch if blank' },
  ],
};

const ID_PREFIX: Record<NewSource['kind'], string> = {
  sharepoint: 'sp',
  confluence: 'confluence',
  bitbucket: 'bitbucket',
};

function sourceId(kind: NewSource['kind'], name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .replace(new RegExp(`^${kind}-`), '');
  return `${ID_PREFIX[kind]}-${slug}`.slice(0, 63).replace(/-+$/, '');
}

function AddSource({ onDone }: { onDone: () => void }): ReactNode {
  const qc = useQueryClient();
  const [kind, setKind] = useState<NewSource['kind']>('sharepoint');
  const [name, setName] = useState('');
  const [config, setConfig] = useState<Record<string, string>>({});
  const [groups, setGroups] = useState('Trident-AI');
  const input = 'rounded border border-steel-200 px-2 py-1 text-sm focus:border-navy-400 focus:outline-none';

  const add = useMutation({
    mutationFn: () =>
      createSource({
        id: sourceId(kind, name),
        kind,
        display_name: name.trim(),
        config,
        readable_groups: groups.split(',').map((g) => g.trim()).filter(Boolean),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sources'] });
      onDone();
    },
  });
  const ready = name.trim() && groups.trim() && FIELDS[kind].every(
    (f) => f.name === 'branch' || (config[f.name] ?? '').trim(),
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate();
      }}
      className="flex flex-col gap-3 rounded-lg border border-steel-200 bg-white p-4"
      data-testid="add-source"
    >
      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1 text-xs text-steel-600">
          System
          <select
            aria-label="System"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as NewSource['kind']);
              setConfig({});
            }}
            className={input}
          >
            {SECTIONS.map((s) => (
              <option key={s.kind} value={s.kind}>{s.title}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-1 flex-col gap-1 text-xs text-steel-600">
          Name shown here and in answers
          <input aria-label="Name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. SharePoint — TRAVEL" className={input} />
        </label>
      </div>
      {FIELDS[kind].map((f) => (
        <label key={f.name} className="flex flex-col gap-1 text-xs text-steel-600">
          {f.label}
          <input aria-label={f.label} value={config[f.name] ?? ''}
            onChange={(e) => setConfig({ ...config, [f.name]: e.target.value })}
            className={input} />
          {f.hint && <span className="text-steel-400">{f.hint}</span>}
        </label>
      ))}
      <label className="flex flex-col gap-1 text-xs text-steel-600">
        Readable by (groups, comma-separated)
        <input aria-label="Readable by" value={groups} onChange={(e) => setGroups(e.target.value)}
          className={input} />
        <span className="text-steel-400">
          Everyone in these groups can search this source.
          {kind === 'sharepoint' && ' SharePoint files are further limited to people who can open them in SharePoint.'}
        </span>
      </label>
      {add.error && (
        <p className="text-xs text-rose-600">
          {add.error instanceof Error ? add.error.message : 'Could not add the source.'}
        </p>
      )}
      <div className="flex gap-2">
        <button type="submit" disabled={!ready || add.isPending}
          className="rounded-lg bg-navy-700 px-3 py-1 text-sm font-medium text-white hover:bg-navy-800 disabled:opacity-50">
          {add.isPending ? 'Adding…' : 'Add source'}
        </button>
        <button type="button" onClick={onDone} className="rounded-lg px-3 py-1 text-sm text-steel-600">
          Cancel
        </button>
      </div>
    </form>
  );
}

export function Connectors(): ReactNode {
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const query = useQuery({
    queryKey: ['sources'],
    queryFn: fetchSources,
    retry: false,
    // Background syncs (SharePoint) report progress in the status line.
    refetchInterval: (q) =>
      q.state.data?.some((s) => s.last_sync_status?.startsWith('syncing')) ? 5000 : false,
  });
  const forbidden = query.error instanceof ApiError && query.error.status === 403;

  return (
    <div className="flex flex-col gap-5 animate-fade-in" data-testid="connectors-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-navy-900">Connectors</h2>
          <p className="mt-1 text-sm text-steel-600">
            Knowledge sources synced into the index. Documents are readable only
            by the groups tagged on their source, and SharePoint files only by
            people who can also open them in SharePoint. Adding sources, tag
            changes and syncs are recorded in the audit trail.
          </p>
        </div>
        {query.data && !adding && (
          <button type="button" onClick={() => { setAdding(true); setAdded(false); }}
            className="rounded-lg bg-navy-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-navy-800">
            Add source
          </button>
        )}
      </header>

      {adding && <AddSource onDone={() => { setAdding(false); setAdded(true); }} />}
      {added && !adding && (
        <p className="text-sm text-emerald-700">Source added. Click Sync now on it to index it.</p>
      )}

      {forbidden && (
        <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          Connectors are restricted to platform administrators.
        </div>
      )}
      {query.isLoading && <p className="text-sm text-steel-500">Loading…</p>}
      {query.isError && !forbidden && (
        <p className="text-sm text-rose-600">Failed to load sources.</p>
      )}

      {query.data &&
        [...new Set([
          ...SECTIONS.map((s) => s.kind),
          ...query.data.map((s) => s.kind),
        ])]
          .map((kind) => ({ kind, sources: query.data.filter((s) => s.kind === kind) }))
          .filter((section) => section.sources.length > 0)
          .map((section) => (
            <section key={section.kind} className="flex flex-col gap-2" aria-label={sectionTitle(section.kind)}>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-steel-600">
                {sectionTitle(section.kind)} ({section.sources.length})
              </h3>
              <div className="grid gap-3 lg:grid-cols-2">
                {section.sources.map((s) => (
                  <SourceCard key={s.id} source={s} />
                ))}
              </div>
            </section>
          ))}
    </div>
  );
}

export default Connectors;
